import { ref } from 'vue'
import type { Ref } from 'vue'
import { persistInsightsSidepaneOpen } from '@app/shared/api/user'

// Sidepane defaults to visible when the user has never toggled it.
function readInitialOpen(): boolean {
  const value =
    window?.Silverfin?.currentUserPreferences?.insights_sidepane_open
  return value === undefined ? true : !!value
}

// Module-scoped + lazy: every call to useInsightsSidepaneVisibility() returns
// the same Ref so the floating sidepane and the subnav toggle share state
// directly through Vue's reactivity. The seed read is deferred to first use
// because the bundle runs in <head> while #current_user_preferences (which
// backs window.Silverfin.currentUserPreferences) is rendered in <body>.
let cachedOpen: Ref<boolean> | undefined

function getOpenRef(): Ref<boolean> {
  if (!cachedOpen) {
    cachedOpen = ref<boolean>(readInitialOpen())
  }
  return cachedOpen
}

function setOpen(value: boolean): void {
  const open = getOpenRef()
  if (open.value === value) return

  open.value = value
  void persistInsightsSidepaneOpen(value)
}

function toggle(): void {
  setOpen(!getOpenRef().value)
}

interface InsightsSidepaneVisibility {
  open: Ref<boolean>
  setOpen: (value: boolean) => void
  toggle: () => void
}

export function useInsightsSidepaneVisibility(): InsightsSidepaneVisibility {
  return { open: getOpenRef(), setOpen, toggle }
}
